import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import Cleave from "cleave.js/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import { useQuery } from "@tanstack/react-query";

//Services
import {
  COMPCONTACT_QUERY_KEY,
  companycontactService,
} from "../../../../../Services/companycontactService";

//Types
import { CreateContactPayload } from "../../../../../Types/CompanyProfile/CompanyContact";

const INITIAL_STATE = {
  company_code: "",
  name: "",
  position: "",
  email_address: "",
  mobile1: "",
  mobile2: "",
  telno1: "",
  telno2: "",
  status: -1,
  created_by: undefined,
  updated_by: undefined,
};

interface CreateModalProps {
  toggle?: () => void;
  company_code: string; // required
}

const CreateContactForm: React.FC<CreateModalProps> = ({
  company_code,
  toggle,
}) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(INITIAL_STATE);

  const createContactMutation = useMutation({
    mutationFn: companycontactService.createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPCONTACT_QUERY_KEY });

      toast.success("Contact has been created successfully.");

      setFormData(INITIAL_STATE);

      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        const errorMessage = responseData.errors[firstErrorKey][0];
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error(
          "Unable to create contact. Please verify the entered information.",
        );
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    e.preventDefault();
    if (formData.status === -1) {
      toast.warn("Please select a Status");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const payload = {
      ...formData,
      created_by: user.id,
      updated_by: user.id,
      company_code: company_code,
    };
    createContactMutation.mutate(payload);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter Contact Person Name"
              maxLength={150}
              minLength={3}
              required
              value={formData.name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Job Title / Position<b className="text-danger">*</b>
            </Label>
            <Input
              id="position"
              name="position"
              type="text"
              placeholder="Enter Job Title / Position"
              maxLength={150}
              minLength={3}
              required
              value={formData.position}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Email Address<b className="text-danger">*</b>
            </Label>
            <Input
              id="email_address"
              name="email_address"
              type="email"
              placeholder="Enter Email Address"
              maxLength={150}
              minLength={3}
              required
              value={formData.email_address}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>
              Mobile No.<b className="text-danger">*</b>
            </Label>
            <Cleave
              className="form-control"
              options={{
                delimiter: "-",
                blocks: [3, 4, 3],
                uppercase: true,
              }}
              id="mobile1"
              name="mobile1"
              type="text"
              placeholder="Enter Mobile No."
              maxLength={150}
              minLength={5}
              value={formData.mobile1}
              onChange={handleInputChange}
              required
            />
          </Col>

          <Col md="6">
            <Label>Alt Mobile No.</Label>
            <Cleave
              className="form-control"
              options={{
                delimiter: "-",
                blocks: [3, 4, 3],
                uppercase: true,
              }}
              id="mobile2"
              name="mobile2"
              type="text"
              placeholder="Enter Alternative Mobile No."
              maxLength={150}
              minLength={5}
              value={formData.mobile2}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>Tel No.</Label>
            <Input
              id="telno1"
              name="telno1"
              type="text"
              placeholder="Enter Tel No."
              maxLength={150}
              minLength={3}
              value={formData.telno1}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="6">
            <Label>Alt Tel No.</Label>
            <Input
              id="telno2"
              name="telno2"
              type="text"
              placeholder="Enter Alternative Tel No."
              maxLength={150}
              minLength={3}
              value={formData.telno2}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>

          <Col md="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={createContactMutation.isPending}
            >
              {createContactMutation.isPending
                ? "Creating Contact..."
                : "Create"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateContactForm;
